import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc, Timestamp, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { supabase } from '../config/supabase';

// Admin kullanıcı email'i
const ADMIN_EMAIL = 'datezytestapple@gmail.com';

// Types
interface UserInfo {
  id: string;
  name: string;
  profilePicUrl?: string;
  gender?: string; // 'Kadın', 'Erkek', 'female', 'male'
}

interface Match {
  id: string;
  users: string[];
  user1?: UserInfo;
  user2?: UserInfo;
  lastMessage?: string;
  lastMessageTime?: Timestamp;
  lastMessageSenderId?: string;
  messageCount: number;
  unreadCount?: number;
  createdAt?: Timestamp;
  hasFemaleMessage?: boolean; // Kadın kullanıcı mesaj attı mı?
  femaleUserId?: string; // Kadın kullanıcının ID'si
  isAdminMatch?: boolean; // 🆕 Admin kullanıcı bu match'te mi?
}

interface Message {
  id: string;
  senderId: string;
  content: string;
  messageType: string;
  isRead: boolean;
  timestamp: Timestamp;
  imageUrl?: string;
  giftId?: string;
  giftData?: any;
  sender?: UserInfo;
}

// Global user cache with gender info
const userCache = new Map<string, UserInfo>();
const genderCache = new Map<string, string>(); // userId -> gender

// Skeleton Component
const Skeleton: React.FC<{ width?: string; height?: string; borderRadius?: string }> = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px' 
}) => (
  <div style={{
    width,
    height,
    borderRadius,
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  }} />
);

// Avatar Component
const Avatar: React.FC<{ user?: UserInfo; size?: number }> = ({ user, size = 40 }) => {
  const [imgError, setImgError] = useState(false);
  
  if (user?.profilePicUrl && !imgError) {
    return (
      <img
        src={user.profilePicUrl}
        alt={user.name}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '2px solid #F78D93'
        }}
      />
    );
  }
  
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: '#F78D93',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: size * 0.4,
      border: '2px solid #F78D93'
    }}>
      {user?.name?.charAt(0).toUpperCase() || '?'}
    </div>
  );
};

const MessagesPage: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ totalConversations: 0, totalMessages: 0, activeToday: 0 });
  const [activeTab, setActiveTab] = useState<'all' | 'female' | 'admin'>('all'); // 🆕 Sekme state'i - admin eklendi
  const [femaleStats, setFemaleStats] = useState({ count: 0, messages: 0 }); // 🆕 Kadın kullanıcı istatistikleri
  const [adminStats, setAdminStats] = useState({ count: 0, messages: 0 }); // 🆕 Admin kullanıcı istatistikleri
  const [adminUserId, setAdminUserId] = useState<string | null>(null); // 🆕 Admin kullanıcı ID'si
  const [replyMessage, setReplyMessage] = useState(''); // 🆕 Mesaj gönderme için
  const [sendingMessage, setSendingMessage] = useState(false); // 🆕 Mesaj gönderme durumu
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const unsubscribeMatchesRef = useRef<(() => void) | null>(null);
  const unsubscribeMessagesRef = useRef<(() => void) | null>(null);

  // Get user info with caching (includes gender)
  const getUserInfo = useCallback(async (userId: string): Promise<UserInfo> => {
    if (!userId) return { id: '', name: 'Bilinmiyor' };
    if (userCache.has(userId)) return userCache.get(userId)!;

    try {
      // Try Supabase first - include gender
      const { data: supabaseUser } = await supabase
        .from('users')
        .select('id, name, profile_pic_url, photos, gender')
        .eq('id', userId)
        .single();

      if (supabaseUser) {
        let profilePic = supabaseUser.profile_pic_url;
        if (!profilePic && supabaseUser.photos) {
          const photos = Array.isArray(supabaseUser.photos) 
            ? supabaseUser.photos 
            : JSON.parse(supabaseUser.photos || '[]');
          profilePic = photos[0];
        }
        const gender = supabaseUser.gender || undefined;
        const result: UserInfo = { id: userId, name: supabaseUser.name || 'Bilinmiyor', profilePicUrl: profilePic, gender };
        userCache.set(userId, result);
        if (gender) genderCache.set(userId, gender);
        return result;
      }

      // Fallback to Firebase
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const gender = data.gender || undefined;
        const result: UserInfo = { id: userId, name: data.name || 'Bilinmiyor', profilePicUrl: data.profilePicUrl || data.photos?.[0], gender };
        userCache.set(userId, result);
        if (gender) genderCache.set(userId, gender);
        return result;
      }
    } catch (error) {
      console.error('User fetch error:', userId, error);
    }

    const defaultResult: UserInfo = { id: userId, name: 'Bilinmiyor' };
    userCache.set(userId, defaultResult);
    return defaultResult;
  }, []);

  // Batch fetch users with gender
  const batchFetchUsers = useCallback(async (userIds: string[]) => {
    const uniqueIds = Array.from(new Set(userIds.filter(id => id && !userCache.has(id))));
    if (uniqueIds.length === 0) return;

    try {
      // Batch fetch from Supabase with gender
      const { data: users } = await supabase
        .from('users')
        .select('id, name, profile_pic_url, photos, gender')
        .in('id', uniqueIds);

      if (users) {
        users.forEach(user => {
          let profilePic = user.profile_pic_url;
          if (!profilePic && user.photos) {
            const photos = Array.isArray(user.photos) ? user.photos : JSON.parse(user.photos || '[]');
            profilePic = photos[0];
          }
          const gender = user.gender || undefined;
          userCache.set(user.id, { id: user.id, name: user.name || 'Bilinmiyor', profilePicUrl: profilePic, gender });
          if (gender) genderCache.set(user.id, gender);
        });
      }
    } catch (error) {
      console.error('Batch fetch users error:', error);
    }
  }, []);

  // 🆕 Admin kullanıcı ID'sini bul
  const findAdminUserId = useCallback(async () => {
    try {
      // Supabase'den admin kullanıcıyı bul
      const { data: adminUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', ADMIN_EMAIL)
        .single();

      if (adminUser) {
        setAdminUserId(adminUser.id);
        console.log('Admin user found:', adminUser.id);
        return adminUser.id;
      }
      
      console.log('Admin user not found in Supabase, will search in matches');
      return null;
    } catch (error) {
      console.error('Error finding admin user:', error);
      return null;
    }
  }, []);

  // 🆕 Mesaj gönderme fonksiyonu (Admin olarak)
  const sendMessageAsAdmin = useCallback(async (matchId: string, content: string) => {
    if (!adminUserId || !content.trim() || !selectedMatch) return;
    
    setSendingMessage(true);
    try {
      // Firebase'e mesaj ekle
      const messagesRef = collection(db, 'matches', matchId, 'messages');
      await addDoc(messagesRef, {
        senderId: adminUserId,
        content: content.trim(),
        messageType: 'text',
        isRead: false,
        timestamp: serverTimestamp()
      });

      // Match dökümanını güncelle (lastMessage, lastMessageTime)
      const matchRef = doc(db, 'matches', matchId);
      await updateDoc(matchRef, {
        lastMessage: content.trim(),
        lastMessageTime: serverTimestamp(),
        lastMessageSenderId: adminUserId
      });

      setReplyMessage('');
      console.log('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Mesaj gönderilemedi: ' + (error as Error).message);
    } finally {
      setSendingMessage(false);
    }
  }, [adminUserId, selectedMatch]);

  // Load matches with real-time updates
  const loadMatches = useCallback(async (currentAdminUserId?: string | null) => {
    setLoading(true);
    
    try {
      // Unsubscribe from previous listener
      if (unsubscribeMatchesRef.current) {
        unsubscribeMatchesRef.current();
      }

      const matchesQuery = query(
        collection(db, 'matches'),
        orderBy('lastMessageTime', 'desc'),
        limit(100)
      );

      // Real-time listener
      unsubscribeMatchesRef.current = onSnapshot(matchesQuery, async (snapshot) => {
        const matchesData: Match[] = [];
        const allUserIds: string[] = [];
        let totalMessages = 0;
        let activeToday = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // First pass - collect all user IDs
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const users = data.users || [data.user1Id, data.user2Id].filter(Boolean);
          allUserIds.push(...users);
        });

        // Batch fetch all users
        await batchFetchUsers(allUserIds);

        // Second pass - build matches with cached users
        let femaleConversationCount = 0;
        let femaleMessageCount = 0;
        let adminConversationCount = 0;
        let adminMessageCount = 0;
        
        for (const matchDoc of snapshot.docs) {
          const data = matchDoc.data();
          const users = data.users || [data.user1Id, data.user2Id].filter(Boolean);
          
          // Skip system users
          if (users.some((u: string) => u?.includes('datezy_system_user'))) continue;

          const user1 = userCache.get(users[0]) || await getUserInfo(users[0]);
          const user2 = userCache.get(users[1]) || await getUserInfo(users[1]);

          // Skip if system user names
          if (user1.name.includes('Asistan') || user2.name.includes('Asistan')) continue;

          // Get message count (cached in match doc or estimate)
          const messageCount = data.messageCount || 1;
          totalMessages += messageCount;

          // Check if active today
          if (data.lastMessageTime) {
            try {
              const lastTime = data.lastMessageTime.toDate ? data.lastMessageTime.toDate() : new Date(data.lastMessageTime);
              if (lastTime >= today) activeToday++;
            } catch {}
          }

          // 🆕 Check if any female user sent a message
          const isFemale = (gender?: string) => gender === 'Kadın' || gender === 'female' || gender === 'Female';
          const user1IsFemale = isFemale(user1.gender);
          const user2IsFemale = isFemale(user2.gender);
          
          // Check if the last message sender is female OR if either user is female
          // For accurate detection, we'd need to check all messages, but for performance
          // we use lastMessageSenderId to see if a female sent any message
          let hasFemaleMessage = false;
          let femaleUserId: string | undefined;
          
          if (user1IsFemale) {
            hasFemaleMessage = true;
            femaleUserId = user1.id;
          }
          if (user2IsFemale) {
            hasFemaleMessage = true;
            femaleUserId = user2.id;
          }
          
          // Count female conversations
          if (hasFemaleMessage) {
            femaleConversationCount++;
            femaleMessageCount += messageCount;
          }

          // 🆕 Check if admin user is in this match
          const isAdminMatch = currentAdminUserId && users.includes(currentAdminUserId);
          if (isAdminMatch) {
            adminConversationCount++;
            adminMessageCount += messageCount;
          }

          matchesData.push({
            id: matchDoc.id,
            users,
            user1,
            user2,
            lastMessage: data.lastMessage,
            lastMessageTime: data.lastMessageTime,
            lastMessageSenderId: data.lastMessageSenderId,
            messageCount,
            createdAt: data.createdAt,
            hasFemaleMessage,
            femaleUserId,
            isAdminMatch // 🆕 Admin match flag
          });
        }

        setMatches(matchesData);
        setStats({ totalConversations: matchesData.length, totalMessages, activeToday });
        setFemaleStats({ count: femaleConversationCount, messages: femaleMessageCount });
        setAdminStats({ count: adminConversationCount, messages: adminMessageCount }); // 🆕
        setLoading(false);
      }, (error) => {
        console.error('Matches listener error:', error);
        setLoading(false);
      });

    } catch (error) {
      console.error('Load matches error:', error);
      setLoading(false);
    }
  }, [batchFetchUsers, getUserInfo]);

  // Load messages with real-time updates
  const loadMessages = useCallback(async (matchId: string, match: Match) => {
    setMessagesLoading(true);
    
    try {
      // Unsubscribe from previous listener
      if (unsubscribeMessagesRef.current) {
        unsubscribeMessagesRef.current();
      }

      const messagesQuery = query(
        collection(db, 'matches', matchId, 'messages'),
        orderBy('timestamp', 'asc'),
        limit(500)
      );

      // Real-time listener for messages
      unsubscribeMessagesRef.current = onSnapshot(messagesQuery, (snapshot) => {
        const messagesData: Message[] = snapshot.docs.map(msgDoc => {
          const data = msgDoc.data();
          
          let sender: UserInfo | undefined;
          if (data.senderId === match.users[0]) {
            sender = match.user1;
          } else if (data.senderId === match.users[1]) {
            sender = match.user2;
          }

          return {
            id: msgDoc.id,
            senderId: data.senderId,
            content: data.content || '',
            messageType: data.messageType || 'text',
            isRead: data.isRead || false,
            timestamp: data.timestamp,
            imageUrl: data.imageUrl,
            giftId: data.giftId,
            giftData: data.giftData,
            sender
          };
        });

        setMessages(messagesData);
        setMessagesLoading(false);
        
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }, (error) => {
        console.error('Messages listener error:', error);
        setMessagesLoading(false);
      });

    } catch (error) {
      console.error('Load messages error:', error);
      setMessagesLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const initializeData = async () => {
      // Önce admin kullanıcı ID'sini bul
      const foundAdminId = await findAdminUserId();
      // Sonra matches'i yükle
      loadMatches(foundAdminId);
    };
    
    initializeData();
    
    return () => {
      if (unsubscribeMatchesRef.current) unsubscribeMatchesRef.current();
      if (unsubscribeMessagesRef.current) unsubscribeMessagesRef.current();
    };
  }, [loadMatches, findAdminUserId]);

  // Handle match selection
  const handleSelectMatch = (match: Match) => {
    setSelectedMatch(match);
    loadMessages(match.id, match);
  };

  // Filter matches based on search and tab
  const filteredMatches = matches.filter(match => {
    // Tab filter - Kadın kullanıcılar sekmesi
    if (activeTab === 'female' && !match.hasFemaleMessage) {
      return false;
    }
    
    // 🆕 Tab filter - Admin sohbetleri sekmesi
    if (activeTab === 'admin' && !match.isAdminMatch) {
      return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      match.user1?.name?.toLowerCase().includes(searchLower) ||
      match.user2?.name?.toLowerCase().includes(searchLower)
    );
  });

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      
      if (diff < 60000) return 'Az önce';
      if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
      if (diff < 86400000) return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
      
      return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    } catch {
      return '-';
    }
  };

  // Message type icon
  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return '📷';
      case 'gift': return '🎁';
      case 'voice': return '🎤';
      case 'super_message': return '⭐';
      default: return '';
    }
  };

  return (
    <div style={{ padding: '0' }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .conversation-item:hover {
          background-color: #f8f9fa !important;
          transform: translateX(4px);
        }
        .message-bubble {
          animation: fadeIn 0.3s ease-out;
        }
        .online-indicator {
          animation: pulse 2s infinite;
        }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '2rem',
        marginBottom: '1.5rem',
        color: 'white'
      }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.8rem', fontWeight: '600' }}>
          💬 Mesajlar
        </h2>
        
        {/* Stats Cards */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            minWidth: '140px'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {loading ? <Skeleton width="50px" height="32px" /> : stats.totalConversations}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Konuşma</div>
          </div>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            minWidth: '140px'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {loading ? <Skeleton width="50px" height="32px" /> : stats.totalMessages}
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Toplam Mesaj</div>
          </div>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            minWidth: '140px'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {loading ? <Skeleton width="50px" height="32px" /> : stats.activeToday}
              <span className="online-indicator" style={{ 
                width: '10px', 
                height: '10px', 
                backgroundColor: '#4ade80', 
                borderRadius: '50%',
                display: 'inline-block'
              }} />
            </div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Bugün Aktif</div>
          </div>
        </div>
      </div>

      {/* 🆕 Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        backgroundColor: 'white',
        padding: '0.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            flex: 1,
            padding: '0.875rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.2s ease',
            backgroundColor: activeTab === 'all' ? '#667eea' : 'transparent',
            color: activeTab === 'all' ? 'white' : '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          💬 Tüm Konuşmalar
          <span style={{
            backgroundColor: activeTab === 'all' ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.85rem'
          }}>
            {stats.totalConversations}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('female')}
          style={{
            flex: 1,
            padding: '0.875rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.2s ease',
            backgroundColor: activeTab === 'female' ? '#ec4899' : 'transparent',
            color: activeTab === 'female' ? 'white' : '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          👩 Kadın Kullanıcılar
          <span style={{
            backgroundColor: activeTab === 'female' ? 'rgba(255,255,255,0.2)' : '#fce7f3',
            color: activeTab === 'female' ? 'white' : '#ec4899',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: 'bold'
          }}>
            {femaleStats.count}
          </span>
        </button>
        {/* 🆕 Admin Sohbetleri Sekmesi */}
        <button
          onClick={() => setActiveTab('admin')}
          style={{
            flex: 1,
            padding: '0.875rem 1.5rem',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.2s ease',
            backgroundColor: activeTab === 'admin' ? '#10b981' : 'transparent',
            color: activeTab === 'admin' ? 'white' : '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          👨‍💼 Moderatör
          <span style={{
            backgroundColor: activeTab === 'admin' ? 'rgba(255,255,255,0.2)' : '#d1fae5',
            color: activeTab === 'admin' ? 'white' : '#10b981',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: 'bold'
          }}>
            {adminStats.count}
          </span>
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedMatch ? '380px 1fr' : '1fr',
        gap: '1.5rem',
        height: 'calc(100vh - 400px)',
        minHeight: '500px',
        transition: 'grid-template-columns 0.3s ease'
      }}>
        {/* Conversations List */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Search */}
          <div style={{ padding: '1rem', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: '2px solid #f0f0f0',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#f0f0f0'}
              />
            </div>
          </div>

          {/* Conversations */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
            {loading ? (
              // Loading skeletons
              [...Array(8)].map((_, i) => (
                <div key={i} style={{ padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <Skeleton width="48px" height="48px" borderRadius="50%" />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="70%" height="16px" />
                    <div style={{ marginTop: '8px' }}>
                      <Skeleton width="90%" height="12px" />
                    </div>
                  </div>
                </div>
              ))
            ) : filteredMatches.length === 0 ? (
              <div style={{ 
                padding: '3rem', 
                textAlign: 'center', 
                color: '#999'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                <p style={{ margin: 0 }}>Henüz mesaj yok</p>
              </div>
            ) : (
              filteredMatches.map((match, index) => (
                <div
                  key={match.id}
                  className="conversation-item"
                  onClick={() => handleSelectMatch(match)}
                  style={{
                    padding: '0.875rem',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    backgroundColor: selectedMatch?.id === match.id ? '#f0f4ff' : 'transparent',
                    border: selectedMatch?.id === match.id ? '2px solid #667eea' : '2px solid transparent',
                    marginBottom: '0.5rem',
                    transition: 'all 0.2s ease',
                    animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {/* Avatars */}
                    <div style={{ position: 'relative', width: '56px', flexShrink: 0 }}>
                      <Avatar user={match.user1} size={36} />
                      <div style={{ position: 'absolute', bottom: -4, right: 0 }}>
                        <Avatar user={match.user2} size={28} />
                      </div>
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '0.25rem'
                      }}>
                        <span style={{ 
                          fontWeight: '600', 
                          color: '#1a1a2e', 
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {match.user1?.name || 'Bilinmiyor'} & {match.user2?.name || 'Bilinmiyor'}
                        </span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#999',
                          flexShrink: 0,
                          marginLeft: '0.5rem'
                        }}>
                          {formatDate(match.lastMessageTime)}
                        </span>
                      </div>
                      
                      {match.lastMessage && (
                        <p style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          color: '#666',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {match.lastMessage.length > 50 
                            ? match.lastMessage.substring(0, 50) + '...' 
                            : match.lastMessage}
                        </p>
                      )}

                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        marginTop: '0.25rem',
                        flexWrap: 'wrap'
                      }}>
                        {/* 🆕 Admin indicator */}
                        {match.isAdminMatch && (
                          <span style={{ 
                            fontSize: '0.7rem', 
                            backgroundColor: '#d1fae5', 
                            color: '#10b981',
                            padding: '0.125rem 0.5rem', 
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            👨‍💼 Moderatör
                          </span>
                        )}
                        {/* 🆕 Female indicator */}
                        {match.hasFemaleMessage && (
                          <span style={{ 
                            fontSize: '0.7rem', 
                            backgroundColor: '#fce7f3', 
                            color: '#ec4899',
                            padding: '0.125rem 0.5rem', 
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            👩 {match.user1?.gender === 'Kadın' || match.user1?.gender === 'female' ? match.user1?.name?.split(' ')[0] : match.user2?.name?.split(' ')[0]}
                          </span>
                        )}
                        <span style={{ 
                          fontSize: '0.7rem', 
                          backgroundColor: '#f0f0f0', 
                          padding: '0.125rem 0.5rem', 
                          borderRadius: '4px',
                          color: '#666'
                        }}>
                          💬 {match.messageCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages Panel */}
        {selectedMatch && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            {/* Chat Header */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)'
            }}>
              <button
                onClick={() => {
                  setSelectedMatch(null);
                  if (unsubscribeMessagesRef.current) unsubscribeMessagesRef.current();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ←
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Avatar user={selectedMatch.user1} size={40} />
                <span style={{ color: '#F78D93', fontSize: '1.2rem' }}>💕</span>
                <Avatar user={selectedMatch.user2} size={40} />
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#1a1a2e' }}>
                  {selectedMatch.user1?.name} & {selectedMatch.user2?.name}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {messages.length} mesaj
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '1rem',
              backgroundColor: '#f8f9fa'
            }}>
              {messagesLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} style={{ 
                      display: 'flex', 
                      justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end' 
                    }}>
                      <Skeleton width="60%" height="50px" borderRadius="12px" />
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                  <p>Henüz mesaj yok</p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isUser1 = message.senderId === selectedMatch.users[0];
                  
                  return (
                    <div
                      key={message.id}
                      className="message-bubble"
                      style={{
                        display: 'flex',
                        flexDirection: isUser1 ? 'row' : 'row-reverse',
                        alignItems: 'flex-end',
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <Avatar user={message.sender} size={28} />
                      <div style={{
                        maxWidth: '70%',
                        padding: '0.75rem 1rem',
                        borderRadius: isUser1 ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                        backgroundColor: isUser1 ? 'white' : '#667eea',
                        color: isUser1 ? '#1a1a2e' : 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>
                          {message.sender?.name || 'Bilinmiyor'}
                        </div>
                        
                        {message.messageType === 'image' && message.imageUrl ? (
                          <img 
                            src={message.imageUrl} 
                            alt="Gönderilen görsel"
                            style={{ maxWidth: '200px', borderRadius: '8px' }}
                          />
                        ) : message.messageType === 'gift' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            🎁 <span>Hediye gönderildi</span>
                          </div>
                        ) : (
                          <div>{getMessageTypeIcon(message.messageType)} {message.content}</div>
                        )}
                        
                        <div style={{ 
                          fontSize: '0.65rem', 
                          opacity: 0.6, 
                          marginTop: '0.25rem',
                          textAlign: 'right'
                        }}>
                          {formatDate(message.timestamp)}
                          {message.isRead && ' ✓✓'}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 🆕 Mesaj Gönderme Alanı - Sadece Admin sekmesinde */}
            {activeTab === 'admin' && adminUserId && (
              <div style={{
                padding: '1rem',
                borderTop: '1px solid #f0f0f0',
                backgroundColor: 'white',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  placeholder="Mesaj yaz..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessageAsAdmin(selectedMatch.id, replyMessage);
                    }
                  }}
                  disabled={sendingMessage}
                  style={{
                    flex: 1,
                    padding: '0.875rem 1rem',
                    border: '2px solid #f0f0f0',
                    borderRadius: '24px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: sendingMessage ? '#f5f5f5' : 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#10b981'}
                  onBlur={(e) => e.target.style.borderColor = '#f0f0f0'}
                />
                <button
                  onClick={() => sendMessageAsAdmin(selectedMatch.id, replyMessage)}
                  disabled={sendingMessage || !replyMessage.trim()}
                  style={{
                    padding: '0.875rem 1.5rem',
                    backgroundColor: sendingMessage || !replyMessage.trim() ? '#ccc' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '24px',
                    cursor: sendingMessage || !replyMessage.trim() ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {sendingMessage ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid white', 
                        borderTopColor: 'transparent', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite' 
                      }} />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      📤 Gönder
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State when no match selected */}
        {!selectedMatch && !loading && matches.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '1rem',
            color: '#999'
          }}>
            <div style={{ fontSize: '4rem' }}>👈</div>
            <p style={{ margin: 0, fontSize: '1.1rem' }}>Bir konuşma seçin</p>
            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.7 }}>
              Mesajları görüntülemek için soldaki listeden bir konuşma seçin
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
